'use client'
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useId, useState } from 'react'
import { Controller, UseFormRegisterReturn } from 'react-hook-form'
import FormError from '../FormError'
import Label from '../Label'
import { inputClass as fieldClass } from './classes'

export interface IInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name'> {
    name?: string
    labelText?: string
    isRequired?: boolean
    /** Mensagem de erro (padrão do orbit, com `register`). Com `control`, o erro vem do próprio campo. */
    error?: string
    register?: UseFormRegisterReturn
    /**
     * Campo controlado pelo `Controller`, só para o mesmo campo renderizado duas vezes no formulário (a senha nas colunas
     * de desktop e de celular do `EditCustomerFormCheckout`): com `register`, os dois inputs disputariam o mesmo ref e
     * um deles ficaria com o valor desatualizado. Nos demais casos, `register`.
     */
    control?: any
    inputClass?: string
}

interface IFieldProps extends Omit<IInputProps, 'control' | 'register' | 'error'> {
    field: Record<string, unknown>
    error?: string
}

const Field: React.FC<IFieldProps> = ({
    field,
    error,
    labelText,
    isRequired,
    className,
    inputClass = '',
    type = 'text',
    id,
    ...rest
}) => {
    const [showPassword, setShowPassword] = useState(false)
    const isPassword = type === 'password'
    const generatedId = useId()
    const inputId = id ?? generatedId
    const errorId = `${inputId}-error`

    return (
        <Label
            htmlFor={inputId}
            labelText={labelText}
            isRequired={isRequired}
            hasError={Boolean(error)}
            className={className}
        >
            <div className="relative">
                <input
                    {...field}
                    id={inputId}
                    className={`${inputClass} ${fieldClass(Boolean(error))}`}
                    type={isPassword ? (showPassword ? 'text' : 'password') : type}
                    max={rest.maxLength}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? errorId : undefined}
                    {...rest}
                />
                {isPassword && (
                    // biome-ignore lint/a11y/useSemanticElements: um <button> centraliza o ícone na altura e o desloca (visual de hoje mantido)
                    <span
                        role="button"
                        tabIndex={0}
                        aria-label={showPassword ? 'Esconder senha' : 'Mostrar senha'}
                        className="absolute bottom-0 cursor-pointer right-4 top-3"
                        onClick={() => setShowPassword((p) => !p)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setShowPassword((p) => !p)
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} size="sm" className="color-primary" />
                    </span>
                )}
            </div>
            {error && <FormError id={errorId}>{error}</FormError>}
        </Label>
    )
}

/** Campo de texto da loja (padrão do orbit: `labelText`, `isRequired`, `error`, `register`; `control` só no campo duplicado). */
const Input: React.FC<IInputProps> = ({ control, register, name, error, maxLength, ...props }) => {
    if (control && name) {
        return (
            <Controller
                name={name}
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                    <Field field={field} error={fieldState.error?.message} maxLength={maxLength} {...props} />
                )}
            />
        )
    }
    return <Field field={{ ...register }} error={error} maxLength={maxLength} {...props} />
}

export default Input
